import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../api/client';

type Step = 'credentials' | 'otp';

interface OtpChallenge {
  challenge_id: string;
  masked_email: string;
  expires_in_seconds: number;
}

function formatSecondsAsMMSS(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function Login() {
  const { user, login, verifyOtp, resendOtp, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<Step>('credentials');
  const [challenge, setChallenge] = useState<OtpChallenge | null>(null);
  const [otpDigits, setOtpDigits] = useState<string[]>(['', '', '', '', '', '']);
  const digitRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Cleanup must run on every render path. Keep this hook BEFORE the
  // `if (user) return <Navigate/>` guard below — moving it after the early
  // return changes the hook count between renders and triggers React's
  // "Rendered fewer hooks than expected" error the moment `user` becomes set.
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  if (!isLoading && user) {
    const dest = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={dest} replace />;
  }

  function startExpiryTimer(seconds: number) {
    setSecondsLeft(seconds);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) { clearInterval(timerRef.current!); return 0; }
        return s - 1;
      });
    }, 1000);
  }

  function startResendCooldown() {
    setResendCooldown(30);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) { clearInterval(resendTimerRef.current!); return 0; }
        return s - 1;
      });
    }, 1000);
  }

  function handleDigitChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...otpDigits];
    next[index] = digit;
    setOtpDigits(next);
    setError(null);
    if (digit && index < 5) digitRefs.current[index + 1]?.focus();
    if (digit && next.every((d) => d !== '')) handleOtpSubmit(next.join(''));
  }

  function handleDigitKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otpDigits[index] && index > 0) digitRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowLeft' && index > 0) digitRefs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < 5) digitRefs.current[index + 1]?.focus();
  }

  function handleDigitPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      e.preventDefault();
      setOtpDigits(pasted.split(''));
      digitRefs.current[5]?.focus();
      handleOtpSubmit(pasted);
    }
  }

  async function handleCredentialsSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const result = await login(email, password);
      if (!result.mfa_required) {
        if (result.must_change_password) { navigate('/change-password', { replace: true }); return; }
        const dest = (location.state as { from?: string } | null)?.from ?? '/';
        navigate(dest, { replace: true });
        return;
      }
      setChallenge({
        challenge_id: result.challenge_id!,
        masked_email: result.masked_email!,
        expires_in_seconds: result.expires_in_seconds!,
      });
      setStep('otp');
      startExpiryTimer(result.expires_in_seconds!);
      startResendCooldown();
      setTimeout(() => digitRefs.current[0]?.focus(), 50);
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.code === 'ACCOUNT_LOCKED' ? 'This account is locked after too many failed sign-ins. Ask an administrator to reset it.' : e.status === 401 ? 'Invalid email or password' : (e.message ?? 'Sign in failed'));
      } else {
        setError('Sign in failed — check your network connection');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleOtpSubmit(code?: string) {
    const otpCode = code ?? otpDigits.join('');
    if (otpCode.length !== 6 || !challenge) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await verifyOtp(challenge.challenge_id, otpCode);
      if (result.must_change_password) {
        navigate('/change-password', { replace: true });
      } else {
        const dest = (location.state as { from?: string } | null)?.from ?? '/';
        navigate(dest, { replace: true });
      }
    } catch (e) {
      setOtpDigits(['', '', '', '', '', '']);
      digitRefs.current[0]?.focus();
      if (e instanceof ApiError) {
        setError(e.message ?? 'Incorrect code');
        if (e.status === 400) {
          setTimeout(() => {
            setStep('credentials');
            setChallenge(null);
            setOtpDigits(['', '', '', '', '', '']);
            setError(null);
          }, 2500);
        }
      } else {
        setError('Verification failed — check your network connection');
      }
    } finally {
      setSubmitting(false);
    }
  }

  async function handleResend() {
    if (!challenge || resendCooldown > 0) return;
    setError(null);
    try {
      const result = await resendOtp(challenge.challenge_id);
      setChallenge({
        challenge_id: result.challenge_id,
        masked_email: challenge.masked_email,
        expires_in_seconds: result.expires_in_seconds,
      });
      setOtpDigits(['', '', '', '', '', '']);
      startExpiryTimer(result.expires_in_seconds);
      startResendCooldown();
      digitRefs.current[0]?.focus();
    } catch (e) {
      if (e instanceof ApiError) {
        setError(e.status === 429 ? 'Too many resend attempts. Please wait.' : (e.message ?? 'Resend failed'));
      }
    }
  }

  return (
    <div className="min-h-screen bg-[#fbfaf7] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-3xl text-ink">QRAM</h1>
          <p className="text-ink-500 text-sm mt-2">Quantum Risk Assessment Module</p>
        </div>

        <div className="bg-white border border-ink-200 rounded p-6 shadow-sm">
          {step === 'credentials' && (
            <>
              <h2 className="text-lg font-display text-ink mb-4">Sign in</h2>

              {error && (
                <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded">
                  {error}
                </div>
              )}

              <form onSubmit={handleCredentialsSubmit}>
                <label className="block mb-3">
                  <span className="text-sm text-ink-700">Email</span>
                  <input
                    type="email"
                    required
                    autoFocus
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent"
                  />
                </label>

                <label className="block mb-4">
                  <span className="text-sm text-ink-700">Password</span>
                  <input
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-1 w-full px-3 py-2 border border-ink-200 rounded text-sm focus:outline-none focus:border-accent"
                  />
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-accent text-white py-2 rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Signing in…' : 'Continue'}
                </button>
              </form>

              <p className="text-xs text-ink-400 mt-4 text-center">
                A 6-digit verification code will be emailed to you.
              </p>
            </>
          )}

          {step === 'otp' && challenge && (
            <>
              <button
                onClick={() => { setStep('credentials'); setError(null); setChallenge(null); }}
                className="text-xs text-ink-400 hover:text-ink-700 mb-4 flex items-center gap-1"
              >
                ← Back to sign in
              </button>

              <h2 className="text-lg font-display text-ink mb-1">Check your email</h2>
              <p className="text-sm text-ink-500 mb-5">
                We sent a 6-digit code to{' '}
                <span className="font-medium text-ink">{challenge.masked_email}</span>
              </p>

              {error && (
                <div role="alert" className="mb-4 p-3 bg-red-50 border border-red-200 text-red-800 text-sm rounded">
                  {error}
                </div>
              )}

              <div className="flex gap-2 justify-center mb-5">
                {otpDigits.map((digit, i) => (
                  <input
                    key={i}
                    ref={(el) => { digitRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    pattern="\d*"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleDigitKeyDown(i, e)}
                    onPaste={i === 0 ? handleDigitPaste : undefined}
                    disabled={submitting}
                    className={[
                      'w-11 h-12 text-center text-xl font-mono border rounded',
                      'focus:outline-none focus:border-accent',
                      digit ? 'border-accent bg-accent/5' : 'border-ink-200',
                      submitting ? 'opacity-50 cursor-not-allowed' : '',
                    ].join(' ')}
                    aria-label={`Digit ${i + 1}`}
                  />
                ))}
              </div>

              {otpDigits.some((d) => !d) && (
                <button
                  onClick={() => handleOtpSubmit()}
                  disabled={submitting || otpDigits.join('').length !== 6}
                  className="w-full bg-accent text-white py-2 rounded text-sm font-medium hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                >
                  {submitting ? 'Verifying…' : 'Verify'}
                </button>
              )}

              <div className="flex items-center justify-between text-xs text-ink-400">
                <span>
                  {secondsLeft > 0 ? (
                    <>Code expires in <span className="font-mono">{formatSecondsAsMMSS(secondsLeft)}</span></>
                  ) : (
                    <span className="text-red-600">Code expired</span>
                  )}
                </span>
                <button
                  onClick={handleResend}
                  disabled={resendCooldown > 0}
                  className="text-accent hover:underline disabled:text-ink-300 disabled:no-underline"
                >
                  {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
                </button>
              </div>
            </>
          )}
        </div>

        <p className="text-center text-xs text-ink-300 mt-4">
          🔒 Protected by two-factor authentication
        </p>
      </div>
    </div>
  );
}